import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  useTheme,
  Tooltip,
  Chip,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import BusinessIcon from "@mui/icons-material/Business";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DiscountIcon from "@mui/icons-material/LocalOffer";
import { tokens } from "../../../theme";

const Cart = ({ cart, onRemoveItem, useWholesale }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const isDark = theme.palette.mode === "dark";

  // Lighter blue colors to match parent component — dark-aware
  const lightBlue = {
    main: isDark ? colors.blueAccent[400] : "#5DADE2",
    light: isDark ? colors.primary[300] : "#85C1E9",
    lighter: isDark ? colors.primary[400] : "#AED6F1",
    dark: isDark ? colors.blueAccent[300] : "#3498DB",
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: isDark ? colors.primary[500] : "#F8F9FA",
      }}
    >
      {/* Cart Items Section */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {cart.length > 0 ? (
          <Stack spacing={2}>
            {cart.map((item, index) => {
              const isUsingWholesale = item.isWholesale === true;
              const hasDiscount = item.isDiscount === true && item.discountValue > 0;
              const isExternal = item.isExternalBranch === true || item.isExternal === true;
              const hasPriceOverride = item.isPriceOverride === true && item.overrideAmount > 0;

              return (
                <Paper
                  key={`${item.id}-${index}`}
                  elevation={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 4,
                    },
                    bgcolor: isDark ? colors.primary[400] : "white",
                    position: "relative",
                    // ✅ Border color based on item status
                    ...(isExternal && {
                      border: "2px solid #27AE60",
                    }),
                    ...(hasDiscount && !isExternal && {
                      border: "2px solid #27AE60",
                    }),
                    ...(hasPriceOverride && !isExternal && !hasDiscount && {
                      border: "2px solid #FF7043",
                    }),
                  }}
                >
                  {/* ✅ Status Indicators - Top Right */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      alignItems: "flex-end",
                    }}
                  >
                    {/* ✅ External Sale Indicator */}
                    {isExternal && (
                      <Chip
                        icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                        label="External"
                        size="small"
                        sx={{
                          bgcolor: "#27AE60",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "0.65rem",
                          height: 22,
                        }}
                      />
                    )}

                    {/* ✅ Price Override Indicator */}
                    {hasPriceOverride && (
                      <Chip
                        icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
                        label={`+${item.overrideAmount?.toFixed(0)}`}
                        size="small"
                        sx={{
                          bgcolor: "#FF7043",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "0.65rem",
                          height: 22,
                        }}
                      />
                    )}

                    {/* ✅ Discount Indicator */}
                    {hasDiscount && (
                      <Chip
                        icon={<DiscountIcon sx={{ fontSize: 14 }} />}
                        label={`-${item.discountValue?.toFixed(0)}`}
                        size="small"
                        sx={{
                          bgcolor: "#27AE60",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "0.65rem",
                          height: 22,
                        }}
                      />
                    )}
                  </Box>

                  {/* Product Name */}
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    sx={{ mb: 1, pr: 10 }}
                  >
                    {item.name || item.productName} {item.productCode && `(${item.productCode})`}
                  </Typography>

                  {/* Product Details */}
                  <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                    {/* Color and Size/Size Range - Only show if not external or if external has values */}
                    {item.colorName && item.colorName !== "N/A" && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ minWidth: 45 }}
                        >
                          Color:
                        </Typography>
                        <Chip
                          label={item.colorName}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.75rem",
                            bgcolor: lightBlue.lighter,
                            color: lightBlue.dark,
                          }}
                        />
                      </Box>
                    )}

                    {/* Size or Size Range */}
                    {(item.size || item.sizeRange) && item.size !== "N/A" && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ minWidth: 45 }}
                        >
                          Size:
                        </Typography>
                        <Chip
                          label={item.size || item.sizeRange}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.75rem",
                            bgcolor: lightBlue.lighter,
                            color: lightBlue.dark,
                          }}
                        />
                      </Box>
                    )}

                    {/* Quantity - Show for all items including external */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 45 }}
                      >
                        Qty:
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {item.quantity}{" "}
                        {isExternal ? "unit(s)" : (item.isWarehouse ? "carton(s)" : "pair(s)")}
                      </Typography>
                    </Box>

                    {/* Unit Price - Show original and override if applicable */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 45 }}
                      >
                        Price:
                      </Typography>
                      {hasPriceOverride ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ textDecoration: "line-through", color: "text.secondary" }}
                          >
                            KSh {item.originalSellingPrice?.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" fontWeight="600" sx={{ color: "#FF7043" }}>
                            KSh {item.overridePrice?.toFixed(2)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          KSh {
                            isExternal
                              ? (item.averageSellingPrice / item.quantity)?.toFixed(2)
                              : item.isWarehouse
                                ? item.averageSP?.toFixed(2)
                                : item.averageSellingPrice?.toFixed(2)
                          }{" "}
                          {isExternal ? "/unit" : (item.isWarehouse ? "/carton" : "/pair")}
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  {/* Special Indicators */}
                  <Box
                    sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}
                  >
                    {/* Wholesale Indicator */}
                    {isUsingWholesale && (
                      <Tooltip title="Wholesale price applied" arrow>
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                          label="Wholesale"
                          size="small"
                          sx={{
                            bgcolor: "#4CAF50",
                            color: "white",
                            fontWeight: "600",
                            fontSize: "0.7rem",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Total and Remove Button */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Total
                      </Typography>

                      {/* ✅ Show original, discount, and net amounts */}
                      {hasDiscount ? (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ textDecoration: "line-through", color: "text.secondary" }}
                          >
                            KSh {(item.originalAmount || item.totalPrice)?.toFixed(2)}
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="700"
                            color="#27AE60"
                          >
                            KSh {item.netAmount?.toFixed(2)}
                          </Typography>
                        </Box>
                      ) : hasPriceOverride ? (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ textDecoration: "line-through", color: "text.secondary" }}
                          >
                            KSh {item.originalTotalPrice?.toFixed(2)}
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="700"
                            color="#FF7043"
                          >
                            KSh {item.totalPrice?.toFixed(2)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          variant="h6"
                          fontWeight="700"
                          color={isExternal ? "#27AE60" : lightBlue.dark}
                        >
                          KSh {item.totalPrice?.toFixed(2)}
                        </Typography>
                      )}
                    </Box>

                    <Tooltip title="Remove from cart" arrow>
                      <IconButton
                        onClick={() => onRemoveItem(index)}
                        size="small"
                        sx={{
                          color: "#E74C3C",
                          "&:hover": {
                            bgcolor: "#FADBD8",
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          // Empty Cart State
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              py: 8,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: isDark ? colors.primary[400] : lightBlue.lighter,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 40, color: lightBlue.main }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your cart is empty
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Add items to get started
            </Typography>
          </Box>
        )}
      </Box>

      {/* Total Section - Always visible at bottom */}
      {cart.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            bgcolor: isDark ? colors.primary[400] : "white",
            borderRadius: 0,
            borderTop: `3px solid ${lightBlue.main}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Total Items:
            </Typography>
            <Typography variant="body1" fontWeight="600">
              {cart.length}
            </Typography>
          </Box>

          {/* ✅ Show price overrides total */}
          {cart.some((item) => item.isPriceOverride) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                color: "#FF7043",
              }}
            >
              <Typography variant="body2">Price Overrides:</Typography>
              <Typography variant="body2" fontWeight="600">
                +KSh {cart
                  .reduce((sum, item) => sum + (item.overrideAmount || 0), 0)
                  .toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* ✅ Show discounts total */}
          {cart.some((item) => item.isDiscount) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                color: "#27AE60",
              }}
            >
              <Typography variant="body2">Total Discounts:</Typography>
              <Typography variant="body2" fontWeight="600">
                -KSh {cart
                  .reduce((sum, item) => sum + (item.discountValue || 0), 0)
                  .toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* ✅ Show external sales count */}
          {cart.some((item) => item.isExternal || item.isExternalBranch) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                color: "#27AE60",
              }}
            >
              <Typography variant="body2">External Sales:</Typography>
              <Typography variant="body2" fontWeight="600">
                {cart.filter((item) => item.isExternal || item.isExternalBranch).length} item(s)
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="600">
              Grand Total:
            </Typography>
            <Typography variant="h5" fontWeight="700" color={lightBlue.dark}>
              KSh {cart.reduce(
                (sum, item) => sum + parseFloat(item.netAmount || item.totalPrice || 0),
                0
              ).toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Cart;